import { AnyAction, Reducer, StoreEnhancer, StoreEnhancerStoreCreator } from 'redux';
import {
  REMOVE_POSITION,
  SYNC_INCOMING_TICKET_STATUS,
  UPDATE_INCOMING_POSITION,
  UPDATE_POSITION,
} from '../constants/actionConstants';

/**
 * DEV-4500: while the GeoTrax tab is hidden, SignalR keeps streaming ~25
 * position frames/second. Every dispatch used to fan out to react-redux
 * subscribers, re-rendering the marker layer nobody could see; the resulting
 * DOM/GC churn degraded the tab until Chrome showed "page isn't responding".
 *
 * This enhancer defers subscriber notifications for the high-frequency
 * position-frame actions below — and ONLY those — while the document is
 * hidden. The dispatches still apply to the store immediately (getState is
 * always current, thunks are unaffected); the withheld fan-out happens once,
 * on the first of: a non-deferrable dispatch, or the visibilitychange back to
 * visible. Every other action type notifies synchronously even while hidden,
 * so render-driven side effects that matter for correctness (permission and
 * region changes, socket group management, messaging) are never delayed.
 */

/** The socket-frame actions dispatched by trackingSocket.onResponse. */
export const POSITION_FRAME_ACTION_TYPES: ReadonlySet<string> = new Set([
  UPDATE_POSITION,
  UPDATE_INCOMING_POSITION,
  REMOVE_POSITION,
  SYNC_INCOMING_TICKET_STATUS,
]);

const documentIsHidden = (): boolean => typeof document !== 'undefined' && document.hidden;

// Redux exposes its observable interop under Symbol.observable (ponyfilled to
// '@@observable'); we override it so observable consumers also go through the
// deferring subscribe rather than the base store's.
const $$observable: any =
  (typeof Symbol === 'function' && (Symbol as any).observable) || '@@observable';

export interface DeferNotificationsOptions {
  /** Injectable for tests; defaults to `document.hidden`. */
  isDocumentHidden?: () => boolean;
  /** Action types whose notifications may be deferred while hidden. */
  deferrableActionTypes?: ReadonlySet<string>;
}

export const createDeferNotificationsWhileHidden =
  ({
    isDocumentHidden = documentIsHidden,
    deferrableActionTypes = POSITION_FRAME_ACTION_TYPES,
  }: DeferNotificationsOptions = {}): StoreEnhancer =>
  (next: StoreEnhancerStoreCreator) =>
  <S, A extends AnyAction>(reducer: Reducer<S, A>, preloadedState?: any) => {
    const store = next(reducer, preloadedState);

    // Subscribers register here instead of on the base store, so notification
    // timing is ours to control. Anything subscribing to the base store
    // directly would bypass deferral — nothing in the app does.
    let listeners: Array<() => void> = [];
    let notifyPending = false;

    const notifyAll = () => {
      // Snapshot so listeners that unsubscribe mid-notify still see this
      // notification, matching redux's own listener semantics.
      listeners.slice().forEach(listener => listener());
    };

    const flushPendingNotification = () => {
      if (!notifyPending) {
        return;
      }
      notifyPending = false;
      detachVisibilityListener();
      notifyAll();
    };

    // The visibilitychange listener exists only while a notification is
    // pending: attached on the first deferred dispatch, detached on flush.
    // This keeps test/HMR store instances from leaking permanent document
    // listeners.
    const onVisibilityChange = () => {
      if (!isDocumentHidden()) {
        flushPendingNotification();
      }
    };
    let visibilityListenerAttached = false;
    const attachVisibilityListener = () => {
      if (visibilityListenerAttached || typeof document === 'undefined') {
        return;
      }
      visibilityListenerAttached = true;
      document.addEventListener('visibilitychange', onVisibilityChange);
    };
    const detachVisibilityListener = () => {
      if (!visibilityListenerAttached) {
        return;
      }
      visibilityListenerAttached = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    const subscribe = (listener: () => void) => {
      if (typeof listener !== 'function') {
        throw new Error('Expected the listener to be a function.');
      }
      listeners.push(listener);
      let isSubscribed = true;
      return function unsubscribe() {
        if (!isSubscribed) {
          return;
        }
        isSubscribed = false;
        // Remove ONE registration, matching redux: the same listener
        // subscribed twice keeps its other registration.
        const index = listeners.indexOf(listener);
        if (index >= 0) {
          listeners = [...listeners.slice(0, index), ...listeners.slice(index + 1)];
        }
      };
    };

    const dispatch = <T extends A>(action: T): T => {
      const result = store.dispatch(action);
      if (isDocumentHidden() && deferrableActionTypes.has(action.type)) {
        notifyPending = true;
        attachVisibilityListener();
      } else {
        // Any synchronous notification renders the latest state, which covers
        // whatever was deferred — clear the pending flush so the next
        // visibilitychange doesn't double-notify.
        notifyPending = false;
        detachVisibilityListener();
        notifyAll();
      }
      return result;
    };

    // replaceReducer must notify through OUR listener list — the base store's
    // internal REPLACE dispatch only reaches base-store subscribers (none).
    const replaceReducer = (nextReducer: Parameters<typeof store.replaceReducer>[0]): void => {
      store.replaceReducer(nextReducer);
      notifyPending = false;
      detachVisibilityListener();
      notifyAll();
    };

    // Mirrors redux's own Symbol.observable implementation, but subscribed
    // through the deferring subscribe so observable consumers honor deferral.
    const observable = () => ({
      subscribe(observer: { next?: (state: S) => void }) {
        if (typeof observer !== 'object' || observer === null) {
          throw new TypeError('Expected the observer to be an object.');
        }
        const observeState = () => {
          if (observer.next) {
            observer.next(store.getState());
          }
        };
        observeState();
        const unsubscribe = subscribe(observeState);
        return { unsubscribe };
      },
      [$$observable]() {
        return this;
      },
    });

    // The computed [$$observable] key can't be expressed as the Store type's
    // [Symbol.observable] signature, so assert the (behaviorally equivalent)
    // shape back to the base store's type.
    return {
      ...store,
      dispatch,
      subscribe,
      replaceReducer,
      [$$observable]: observable,
    } as unknown as typeof store;
  };

export default createDeferNotificationsWhileHidden();
